import { supabase } from '@/integrations/supabase/client';
import { getVisitorId } from '@/hooks/use-visitor-tracking';

export interface Attribution {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  landing_page: string | null;
  referrer: string | null;
}

const STORAGE_KEY = 'mbs_attribution';

const EMPTY: Attribution = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_term: null,
  utm_content: null,
  landing_page: null,
  referrer: null,
};

const clean = (v: string | null): string | null => {
  if (!v) return null;
  const t = v.trim().slice(0, 200);
  return t.length ? t : null;
};

/**
 * First-touch attribution: the first time a visitor arrives with UTM params
 * (or from an external referrer) we persist that context and reuse it for
 * every lead, booking, and CTA event in the session.
 */
export const captureAttribution = (): Attribution => {
  if (typeof window === 'undefined') return EMPTY;

  const params = new URLSearchParams(window.location.search);
  const fromUrl: Attribution = {
    utm_source: clean(params.get('utm_source')),
    utm_medium: clean(params.get('utm_medium')),
    utm_campaign: clean(params.get('utm_campaign')),
    utm_term: clean(params.get('utm_term')),
    utm_content: clean(params.get('utm_content')),
    landing_page: clean(window.location.pathname + window.location.search),
    referrer: clean(document.referrer),
  };

  const hasUtm = Boolean(
    fromUrl.utm_source || fromUrl.utm_medium || fromUrl.utm_campaign ||
    fromUrl.utm_term || fromUrl.utm_content
  );

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const existing: Attribution | null = stored ? { ...EMPTY, ...JSON.parse(stored) } : null;

    // Keep the first touch unless a new campaign click arrives.
    if (existing && !hasUtm) return existing;

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
    return fromUrl;
  } catch {
    return fromUrl;
  }
};

export const getAttribution = (): Attribution => {
  if (typeof window === 'undefined') return EMPTY;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) return { ...EMPTY, ...JSON.parse(stored) };
  } catch {
    /* ignore */
  }
  return captureAttribution();
};

type EventProps = {
  cta_id?: string;
  source?: string;
  metadata?: Record<string, unknown>;
};

/**
 * Records a conversion/CTA event with its campaign attribution.
 * Fire-and-forget — never blocks or breaks the UI.
 */
export const trackEvent = (eventName: string, props: EventProps = {}) => {
  if (typeof window === 'undefined') return;
  const attribution = getAttribution();

  // Google Analytics (if gtag is present on the page)
  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag === 'function') {
    gtag('event', eventName, {
      cta_id: props.cta_id,
      source: props.source,
      campaign_source: attribution.utm_source,
      campaign_medium: attribution.utm_medium,
      campaign_name: attribution.utm_campaign,
      ...props.metadata,
    });
  }

  void supabase
    .from('cta_events')
    .insert({
      visitor_id: getVisitorId(),
      event_name: eventName,
      cta_id: props.cta_id ?? null,
      page_path: window.location.pathname,
      source: props.source ?? null,
      utm_source: attribution.utm_source,
      utm_medium: attribution.utm_medium,
      utm_campaign: attribution.utm_campaign,
      utm_term: attribution.utm_term,
      utm_content: attribution.utm_content,
      referrer: attribution.referrer,
      metadata: (props.metadata ?? null) as never,
    })
    .then(() => {}, () => {});
};
